import React, { useEffect, useState } from "react";
import { CheckCircle2, Phone, CalendarX } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { stageMeta, gbp } from "@/lib/constants";
import { TeamAvatar } from "@/components/TeamAvatar";
import { FollowUpBadge } from "@/components/FollowUpBadge";
import { Button } from "@/components/ui/button";

export default function Followups() {
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState([]);

  const load = () => { api.followupsToday().then(setItems); api.team().then(setTeam); };
  useEffect(() => { load(); }, []);

  const memberById = (id) => team.find((m) => m.id === id);

  const clearFollowup = async (e) => {
    await api.updateEnquiry(e.id, { follow_up_date: null });
    toast.success(`Follow-up cleared for ${e.business_name}`);
    load();
  };

  const advance = async (e) => {
    const order = ["new_enquiry", "intro_call", "proposal_sent", "onboarding", "active_client"];
    const idx = order.indexOf(e.stage);
    const next = order[Math.min(idx + 1, order.length - 1)];
    await api.updateEnquiry(e.id, { stage: next });
    toast.success(`${e.business_name} moved to ${stageMeta(next).label}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="today-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Today's Follow-ups</h1>
        <p className="mt-1 text-sm text-slate-500">Everything due today or overdue, ordered by date.</p>
      </div>

      {items.length === 0 ? (
        <div className="grid place-items-center rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="followups-empty">
          <CheckCircle2 className="h-10 w-10 text-emerald-500" />
          <p className="mt-3 font-display text-lg font-bold text-slate-900">All caught up!</p>
          <p className="text-sm text-slate-500">No pending follow-ups for today.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((e) => {
            const sm = stageMeta(e.stage);
            return (
              <div key={e.id} data-testid={`today-item-${e.id}`} className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
                <div className="flex items-center gap-4">
                  <TeamAvatar member={memberById(e.assignee_id)} size="lg" />
                  <div>
                    <p className="font-semibold text-slate-900">{e.business_name}</p>
                    <p className="text-xs text-slate-500">{e.contact_name}{e.phone ? ` · ${e.phone}` : ""}</p>
                    <div className="mt-1 flex items-center gap-2">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${sm.badge}`}>{sm.label}</span>
                      <FollowUpBadge date={e.follow_up_date} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono-fin text-sm text-slate-600">{gbp(e.deal_value)}</span>
                  <Button variant="outline" size="sm" onClick={() => advance(e)} data-testid={`advance-${e.id}`}>
                    <Phone className="mr-1 h-4 w-4" /> Logged · advance
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => clearFollowup(e)} data-testid={`clear-followup-${e.id}`}>
                    <CalendarX className="mr-1 h-4 w-4 text-slate-400" /> Clear
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
